/**
 * useSessionTimer - Session timer hook with fade in/out
 * Manages countdown, auto-stop, and fade effects
 */
import { useState, useRef, useCallback, useEffect } from 'react';

export const useSessionTimer = (onSessionComplete) => {
  // State
  const [duration, setDuration] = useState(300); // 5 minutes default
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  
  // Fade settings
  const [fadeInDuration, setFadeInDuration] = useState(10); // seconds
  const [fadeOutDuration, setFadeOutDuration] = useState(10); // seconds
  const [fadeEnabled, setFadeEnabled] = useState(true);

  // Refs
  const timerIntervalRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Start timer
  const start = useCallback((customDuration = null) => {
    const dur = customDuration || duration;
    setTimeRemaining(dur);
    setSessionStartTime(Date.now());
    setIsActive(true);
    sessionIdRef.current = `session-${Date.now()}`;

    // Clear any existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
          setIsActive(false);
          
          // Calculate actual duration
          const actualDuration = customDuration || duration;
          if (onSessionComplete) {
            onSessionComplete(actualDuration, sessionIdRef.current);
          }
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    console.log(`⏱️ Session timer started: ${dur} seconds`);
  }, [duration, onSessionComplete]);

  // Stop timer
  const stop = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    const elapsed = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
    
    setIsActive(false);
    setTimeRemaining(0);
    setSessionStartTime(null);
    
    console.log(`⏱️ Session timer stopped after ${elapsed} seconds`);
    return elapsed;
  }, [sessionStartTime]);

  // Pause timer
  const pause = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    console.log('⏱️ Session timer paused');
  }, []);

  // Resume timer
  const resume = useCallback(() => {
    if (!isActive || timeRemaining <= 0) return;

    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
          setIsActive(false);
          
          if (onSessionComplete) {
            onSessionComplete(duration, sessionIdRef.current);
          }
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    console.log('⏱️ Session timer resumed');
  }, [isActive, timeRemaining, duration, onSessionComplete]);

  // Get fade multiplier based on current time
  const getFadeMultiplier = useCallback(() => {
    if (!fadeEnabled || !isActive) return 1;

    const elapsed = sessionStartTime ? (Date.now() - sessionStartTime) / 1000 : 0;
    const totalDuration = duration;

    // Fade in
    if (elapsed < fadeInDuration) {
      return elapsed / fadeInDuration;
    }

    // Fade out
    if (timeRemaining <= fadeOutDuration) {
      return timeRemaining / fadeOutDuration;
    }

    return 1;
  }, [fadeEnabled, isActive, sessionStartTime, duration, fadeInDuration, fadeOutDuration, timeRemaining]);

  // Format time as MM:SS
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  return {
    // State
    duration,
    timeRemaining,
    isActive,
    sessionStartTime,
    fadeEnabled,
    fadeInDuration,
    fadeOutDuration,
    
    // Formatted
    formattedTimeRemaining: formatTime(timeRemaining),
    formattedDuration: formatTime(duration),
    progress: duration > 0 ? ((duration - timeRemaining) / duration) * 100 : 0,
    
    // Setters
    setDuration,
    setFadeEnabled,
    setFadeInDuration,
    setFadeOutDuration,
    
    // Methods
    start,
    stop,
    pause,
    resume,
    getFadeMultiplier,
    formatTime,
  };
};

export default useSessionTimer;
